import { useEffect, useState } from 'react';
import { Button, EmptyState, LoadingState, Modal, SearchInput } from '@/components/ui';
import { mediaAssetsApi } from '@/api/mediaAssets';
import type { MediaAsset } from '@/types/mediaAssets';

interface MediaPickerProps {
  isOpen: boolean;
  tenantId?: string;
  onClose: () => void;
  onSelect: (asset: MediaAsset) => void;
}

// 06.04.26: picker читает только уже опубликованные asset, потому что editor должен
// сохранять канонический public URL, который сразу валиден в preview/runtime.
// Черновики остаются в gallery и не "протекают" в OKDoc по случайному выбору.
export function MediaPicker({ isOpen, tenantId, onClose, onSelect }: MediaPickerProps) {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<MediaAsset[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await mediaAssetsApi.list({
          tenant_id: tenantId,
          search: search || undefined,
          published_only: true,
          limit: 100,
        });
        if (!cancelled) {
          setItems(response.items || []);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, tenantId, search]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Выбрать из галереи" size="xl">
      <div className="space-y-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Поиск по media library..."
        />

        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState
            type="noData"
            title="Нет опубликованных asset"
            description="Сначала загрузите и опубликуйте изображения в tenant gallery."
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => (
              <div key={item.id} className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3">
                {item.media_kind === 'image' && item.public_url ? (
                  <img
                    src={item.public_url}
                    alt={item.alt_text || item.title || item.original_filename}
                    className="h-40 w-full rounded-lg object-cover"
                  />
                ) : item.media_kind === 'video' && item.public_url ? (
                  <video src={item.public_url} controls className="h-40 w-full rounded-lg bg-black object-cover" />
                ) : (
                  <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-neutral-300 bg-neutral-50 text-sm text-neutral-500">
                    {item.media_kind.toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-medium text-neutral-900">{item.title || item.original_filename}</div>
                  <div className="text-xs text-neutral-500">{item.original_filename}</div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onSelect(item);
                    onClose();
                  }}
                  disabled={!item.public_url}
                >
                  Выбрать
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
